import { useConfirmModal } from '@affine/component';
import { AttachmentViewer } from '@affine/core/blocksuite/attachment-viewer';
import { parseDatabaseCellAttachmentSourceId } from '@affine/core/blocksuite/attachment-viewer/database-cell-source-id';
import { parseFullscreenPDFViewerNavigation } from '@affine/core/blocksuite/attachment-viewer/pdf/pdf-navigation';
import type {
  AttachmentTextEditorState,
  AttachmentViewerSource,
} from '@affine/core/blocksuite/attachment-viewer/types';
import {
  normalizeFileCellItems,
  renameFileCellItem,
  replaceFileCellItemBlob,
} from '@affine/core/blocksuite/database-block/properties/file/define';
import { BlobManagementService } from '@affine/core/modules/blob-management/services';
import { EditorService } from '@affine/core/modules/editor';
import { resolveBlobDisplayName } from '@affine/core/utils/blob-display-name';
import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import { createDatabaseBlockDataSource } from '@blocksuite/affine-block-database';
import type { BlockStdScope } from '@blocksuite/std';
import { useService } from '@toeverything/infra';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DocViewStateService } from '../services/doc-view-state';
import type { FullscreenViewProps } from '../services/fullscreen-registry';
import { FullscreenRegistry } from '../services/fullscreen-registry';
import * as styles from './attachment-fullscreen-view.css';
import { buildDatabaseCellPdfSource } from './database-cell-attachment-source';

export const AttachmentFullscreenView: FC<FullscreenViewProps> = ({
  blockId,
  payload,
  onClose,
}) => {
  const editor = useService(EditorService).editor;
  const registry = useService(FullscreenRegistry);
  const docViewState = useService(DocViewStateService);
  const unusedBlobs = useService(BlobManagementService).unusedBlobs;
  const [searchParams] = useSearchParams();
  const { openConfirmModal } = useConfirmModal();
  const [textEditorState, setTextEditorState] =
    useState<AttachmentTextEditorState | null>(null);
  const cleanupReplacedBlob = useCallback(
    (blobId: string) => unusedBlobs.deleteBlobIfUnused(blobId),
    [unusedBlobs]
  );
  const docId = editor.doc.id;
  const page = editor.doc.blockSuiteDoc;
  const block = page.getBlock(blockId);
  const model = block?.model as AttachmentBlockModel | undefined;
  const std = (editor.editorContainer$.value?.host?.std ??
    editor.editorContainer$.value?.std ??
    null) as BlockStdScope | null;
  const source = useAttachmentViewerSource(payload, blockId, docId, page);
  // Stable navigation identity: parse only when the underlying URL params
  // actually change. A new object every render would make the viewer's
  // navigation effects (scroll-to-hit, persist-after-nav, find seeding) re-run
  // on every render and cancel their own scheduled work before it completes.
  const navPageIndexParam =
    searchParams.get('fullscreenAttachmentPageIndex') ?? '';
  const navQueryParam = searchParams.get('fullscreenAttachmentQuery') ?? '';
  const navHighlightRectsParam =
    searchParams.get('fullscreenAttachmentHighlightRects') ?? '';
  const navRequestIdParam =
    searchParams.get('fullscreenAttachmentRequestId') ?? '';
  const navigation = useMemo(
    () => parseFullscreenPDFViewerNavigation(searchParams),
    // searchParams identity changes each render; key on the actual nav values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      navPageIndexParam,
      navQueryParam,
      navHighlightRectsParam,
      navRequestIdParam,
    ]
  );

  const confirmTextSaveBeforeClose = useCallback(() => {
    if (!textEditorState?.dirty) return Promise.resolve(true);
    return new Promise<boolean>(resolve => {
      openConfirmModal({
        title: 'Save changes?',
        description:
          'This text file contains unsaved changes. Save them before leaving the editor.',
        confirmText: 'Save',
        cancelText: 'Continue editing',
        onConfirm: async () => {
          resolve(await textEditorState.save());
        },
        onCancel: () => resolve(false),
      });
    });
  }, [openConfirmModal, textEditorState]);

  useEffect(() => {
    if (!textEditorState?.dirty) return;
    return registry.setCloseGuard(confirmTextSaveBeforeClose);
  }, [confirmTextSaveBeforeClose, registry, textEditorState?.dirty]);

  useEffect(() => {
    if (source) {
      docViewState.update(docId, {
        fullscreen: {
          viewId: 'attachment',
          blockId,
        },
      });
      window.dispatchEvent(
        new CustomEvent('streamlain:fullscreen-attachment-context', {
          detail: {
            action: 'open',
            docId,
            attachmentId: source.id,
            attachmentName: source.name,
            blobKey: source.blobId,
            mimeType: source.mimeType,
          },
        })
      );
      return;
    }

    if (!model || model.flavour !== 'affine:attachment') {
      registry.close();
      return;
    }

    // The navigation params (page/query/highlight/requestId) MUST stay in the
    // URL: the PDF viewer that consumes them only mounts once the document has
    // finished loading (PDFStatus.Opened), which can be several frames after
    // this view mounts. Stripping them here dropped the navigation on a cold
    // open (the searched page was lost → Story 2 regression). One-shot
    // consumption is owned solely by `consumedRequestId` in the persisted
    // viewport state, so the params can safely remain (and keep a bookmarked
    // search URL reproducible — Story 2b).
    docViewState.update(docId, {
      fullscreen: {
        viewId: 'attachment',
        blockId,
      },
    });
    window.dispatchEvent(
      new CustomEvent('streamlain:fullscreen-attachment-context', {
        detail: {
          action: 'open',
          docId,
          attachmentId: blockId,
          attachmentName: resolveBlobDisplayName({
            blobId: model.props.sourceId,
            occurrenceName: model.props.name,
            mimeType: model.props.type,
          }),
          blobKey: model.props.sourceId,
          mimeType: model.props.type,
        },
      })
    );
  }, [blockId, docId, docViewState, model, registry, source]);

  useEffect(() => {
    return () => {
      window.dispatchEvent(
        new CustomEvent('streamlain:fullscreen-attachment-context', {
          detail: {
            action: 'close',
            docId,
            attachmentId: blockId,
          },
        })
      );
      if (registry.consumeUserCloseFlag()) {
        docViewState.clearFullscreen(docId);
      }
    };
  }, [blockId, docId, docViewState, registry]);

  if (!model || model.flavour !== 'affine:attachment') {
    if (source) {
      return (
        <div className={styles.shell}>
          <AttachmentViewer
            source={source}
            navigation={navigation}
            onClose={onClose}
            std={std}
            cleanupReplacedBlob={cleanupReplacedBlob}
            onTextEditorStateChange={setTextEditorState}
          />
        </div>
      );
    }
    return <div className={styles.missing}>Attachment nicht gefunden.</div>;
  }

  return (
    <div className={styles.shell}>
      <AttachmentViewer
        model={model}
        navigation={navigation}
        onClose={onClose}
        std={std}
        cleanupReplacedBlob={cleanupReplacedBlob}
        onTextEditorStateChange={setTextEditorState}
      />
    </div>
  );
};

type DatabasePageLike = {
  getBlock: (id: string) => { model?: unknown } | null | undefined;
};

function useAttachmentViewerSource(
  payload: unknown,
  blockId: string,
  docId: string,
  page: DatabasePageLike
): AttachmentViewerSource | null {
  const fallbackSource = useMemo(
    () => resolveStaticAttachmentViewerSource(payload, blockId, docId),
    [payload, blockId, docId]
  );
  const [resolvedSource, setResolvedSource] =
    useState<AttachmentViewerSource | null>(fallbackSource);

  useEffect(() => {
    setResolvedSource(fallbackSource);
  }, [fallbackSource]);

  useEffect(() => {
    if (isAttachmentViewerSource(payload)) {
      return;
    }
    const parts = parseDatabaseCellAttachmentSourceId(blockId);
    if (!parts) {
      return;
    }
    const databaseBlock = page.getBlock(parts.databaseId);
    const databaseModel = databaseBlock?.model;
    if (!databaseModel) {
      return;
    }

    const dataSource = createDatabaseBlockDataSource(
      databaseModel as Parameters<typeof createDatabaseBlockDataSource>[0]
    );
    const cellValue = dataSource.cellValueGet(parts.rowId, parts.propertyId);
    const canRename =
      !dataSource.readonly$.value &&
      !dataSource.propertyReadonlyGet(parts.propertyId);

    setResolvedSource(
      buildDatabaseCellPdfSource(blockId, docId, {
        cellValue,
        canRename,
        rename: nextName => {
          const currentValue = dataSource.cellValueGet(
            parts.rowId,
            parts.propertyId
          );
          const nextValue = renameFileCellItem(
            normalizeFileCellItems(currentValue),
            parts.refId,
            nextName
          );
          if (!nextValue) {
            return;
          }
          dataSource.cellValueChange(parts.rowId, parts.propertyId, nextValue);
        },
        canReplaceBlob: canRename,
        replaceBlob: replacement => {
          const currentValue = dataSource.cellValueGet(
            parts.rowId,
            parts.propertyId
          );
          const nextValue = replaceFileCellItemBlob(
            normalizeFileCellItems(currentValue),
            parts.refId,
            replacement
          );
          if (!nextValue) {
            return;
          }
          dataSource.cellValueChange(parts.rowId, parts.propertyId, nextValue);
        },
      })
    );

    return () => {
      dataSource.dispose();
    };
  }, [blockId, docId, page, payload]);

  return resolvedSource?.id === fallbackSource?.id
    ? resolvedSource
    : fallbackSource;
}

function resolveStaticAttachmentViewerSource(
  payload: unknown,
  blockId: string,
  docId: string
): AttachmentViewerSource | null {
  if (isAttachmentViewerSource(payload)) {
    return payload;
  }
  return buildDatabaseCellPdfSource(blockId, docId);
}

function isAttachmentViewerSource(
  payload: unknown
): payload is AttachmentViewerSource {
  if (!payload || typeof payload !== 'object') return false;
  const source = payload as Partial<AttachmentViewerSource>;
  return (
    typeof source.id === 'string' &&
    typeof source.docId === 'string' &&
    typeof source.name === 'string' &&
    typeof source.mimeType === 'string' &&
    typeof source.kind === 'string'
  );
}
